import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import * as api from '../../services/api';
import html2pdf from 'html2pdf.js';

/**
 * Marketing Portal: tabbed brochures (clinical vs research partners).
 * Super-admin only for now (see App.tsx).
 * Primary export: client-side PDF for the active tab (no browser URL/date/page footer).
 * Page-break classes / html2pdf__page-break nodes target that exported PDF (html2pdf), not on-screen layout.
 * Optional: system print still available for edge cases.
 */

type MarketingTabId = 'clinical' | 'research';

const PrintStyles = createGlobalStyle`
  /* html2pdf renders the brochure in screen mode; @media print is ignored during capture.
     These rules exist so html2pdf’s “css” + “legacy” pagebreak modes see real break hints. */
  .marketing-print-new-page {
    break-before: page;
    page-break-before: always;
  }
  /* Keep “The problem” + “The solution” on one PDF sheet when they fit (html2pdf css mode). */
  .marketing-problem-solution-sheet {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* Zero footprint on the live page; html2pdf legacy plugin inserts padding after this node in the PDF pipeline only */
  .marketing-pdf-page-break-sentinel {
    height: 0;
    margin: 0;
    padding: 0;
    overflow: hidden;
    line-height: 0;
  }

  /*
   * First PDF page (cover + trust + “The problem”): bump type & spacing so Letter sheet fills better.
   * Scoped under .marketing-pdf-body so html2pdf capture matches on-screen PdfBody (not whole app).
   */
  .marketing-pdf-body .marketing-pdf-page-one-bundle .marketing-pdf-cover {
    padding: 40px 44px 36px;
    margin-bottom: 26px;
    border-radius: 18px;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > p:nth-of-type(1) {
    font-size: 17px !important;
    margin-bottom: 12px !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > h1 {
    font-size: 42px !important;
    line-height: 1.1 !important;
    margin-bottom: 18px !important;
    letter-spacing: -0.03em !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > p:nth-of-type(2) {
    font-size: 18px !important;
    line-height: 1.55 !important;
    margin-bottom: 16px !important;
    max-width: 44rem;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > ul {
    font-size: 16px !important;
    line-height: 1.62 !important;
    margin-bottom: 18px !important;
    column-gap: 32px !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > ul li {
    margin-bottom: 8px !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > p:nth-of-type(3) {
    font-size: 17px !important;
    line-height: 1.55 !important;
    margin-bottom: 20px !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > p:nth-of-type(3) span {
    font-size: clamp(22px, 3.2vw, 30px) !important;
    letter-spacing: 0.1em !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > div:last-of-type {
    gap: 12px 18px !important;
  }
  .marketing-pdf-body .marketing-pdf-cover-inner > div:last-of-type > span {
    font-size: 14px !important;
    padding: 8px 16px !important;
  }
  .marketing-pdf-body .marketing-pdf-trust-strip {
    padding: 18px 0 30px !important;
    margin-bottom: 30px !important;
    gap: 16px 26px !important;
  }
  .marketing-pdf-body .marketing-pdf-trust-strip > div {
    font-size: 15px !important;
    line-height: 1.52 !important;
  }
  .marketing-pdf-body .marketing-pdf-page-one-problem {
    margin-bottom: 32px !important;
  }
  .marketing-pdf-body .marketing-pdf-page-one-problem .marketing-section-kicker.marketing-section-kicker-major {
    font-size: clamp(26px, 4.6vw, 40px) !important;
    margin-bottom: 16px !important;
    padding-left: 16px !important;
    border-left-width: 6px !important;
  }
  .marketing-pdf-body .marketing-pdf-page-one-problem h2 {
    font-size: 26px !important;
    margin-bottom: 16px !important;
  }
  .marketing-pdf-body .marketing-pdf-page-one-problem ul {
    font-size: 17px !important;
    line-height: 1.68 !important;
  }

  @media print {
    @page {
      margin: 14mm 12mm 18mm 12mm;
    }
    html,
    body {
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* Hide app chrome (see AppShell SHELL_CLS) */
    .drschmoe-app-sidebar,
    .drschmoe-app-topbar,
    .drschmoe-app-user-overlay,
    .drschmoe-app-user-panel {
      display: none !important;
    }
    .drschmoe-app-layout {
      display: block !important;
      min-height: 0 !important;
    }
    .drschmoe-app-right-area {
      display: block !important;
      width: 100% !important;
      min-height: 0 !important;
    }
    .drschmoe-app-main {
      overflow: visible !important;
      background: #fff !important;
      padding: 0 !important;
    }
    .marketing-no-print {
      display: none !important;
    }
    .marketing-print-root {
      max-width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    /* Keep section kicker + headline together when possible */
    .marketing-section-heading-block {
      break-after: avoid-page;
      page-break-after: avoid;
    }
    .marketing-section-heading-block h2 {
      orphans: 4;
      widows: 4;
    }
    /* Compact figures so Proof / Outcome sections paginate cleanly */
    .marketing-figure {
      break-inside: avoid;
      page-break-inside: avoid;
      margin: 8px 0 !important;
      box-shadow: none !important;
      border: 1px solid #e5e7eb !important;
    }
    .marketing-fig-head {
      padding: 6px 10px !important;
    }
    .marketing-fig-head .marketing-fig-title {
      font-size: 11pt !important;
    }
    .marketing-fig-head .marketing-fig-sub {
      font-size: 8pt !important;
    }
    .marketing-fig-img-wrap {
      padding: 6px 8px 8px !important;
    }
    .marketing-fig-img {
      max-width: min(100%, 93%) !important;
      width: auto !important;
      max-height: 280px !important;
      height: auto !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }
    .marketing-fig-caption {
      font-size: 8pt !important;
      padding: 0 10px 8px !important;
      line-height: 1.35 !important;
    }
    .marketing-section-kicker {
      font-size: 11pt !important;
    }
    .marketing-section-kicker.marketing-section-kicker-major {
      font-size: 22pt !important;
      padding-left: 10pt !important;
      border-left: 4pt solid #2563eb !important;
      background: linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, transparent 50%) !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const Root = styled.div`
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    'Helvetica Neue',
    Helvetica,
    Arial,
    sans-serif;
  padding: 0 24px 48px;
  max-width: 920px;
  margin: 0 auto;
  color: #0f172a;
`;

/** Wrapped for html2pdf capture (excludes toolbar / super-admin banner) */
const PdfBody = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0 16px;
  margin-bottom: 8px;
  background: linear-gradient(to bottom, #f9fafb 85%, transparent);
  border-bottom: 1px solid #e2e8f0;
`;

const ToolbarHint = styled.span`
  font-size: 13px;
  color: #64748b;
  max-width: 420px;
  line-height: 1.4;
`;

const ExportBtn = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
`;

const PortalHeader = styled.div`
  margin-bottom: 20px;
`;

const PortalTitle = styled.h1`
  font-size: clamp(22px, 3.2vw, 28px);
  font-weight: 800;
  margin: 0 0 6px 0;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const PortalSubtext = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  max-width: 44rem;
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const TabButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
  transition:
    background 0.15s,
    color 0.15s,
    box-shadow 0.15s;
  ${(p) =>
    p.$active
      ? `
    background: #fff;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  `
      : `
    background: transparent;
    color: #64748b;
    &:hover {
      color: #334155;
      background: rgba(255, 255, 255, 0.6);
    }
  `}
`;

const TabAudienceHint = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  line-height: 1.3;
  max-width: 220px;
`;

const Banner = styled.div`
  background: linear-gradient(90deg, #eff6ff 0%, #f0f9ff 100%);
  border: 1px solid #bfdbfe;
  color: #1e3a8a;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const Cover = styled.header`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 32px 36px 28px;
  margin-bottom: 20px;
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 42%, #0c4a6e 100%);
  color: #f8fafc;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.35);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 20% 80%, rgba(56, 189, 248, 0.25) 0%, transparent 45%),
      radial-gradient(circle at 90% 10%, rgba(99, 102, 241, 0.35) 0%, transparent 40%),
      repeating-linear-gradient(
        -12deg,
        transparent,
        transparent 2px,
        rgba(255, 255, 255, 0.03) 2px,
        rgba(255, 255, 255, 0.03) 4px
      );
    pointer-events: none;
  }
`;

const CoverInner = styled.div`
  position: relative;
  z-index: 1;
`;

/** Readable tagline, avoid wide letter-spacing that breaks words across lines in print/PDF */
const CoverTagline = styled.p`
  font-size: 14px;
  color: #7dd3fc;
  margin: 0 0 10px 0;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
`;

const H1 = styled.h1`
  font-size: clamp(26px, 4vw, 36px);
  font-weight: 800;
  margin: 0 0 14px 0;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const Lead = styled.p`
  font-size: 16px;
  color: #cbd5e1;
  line-height: 1.55;
  margin: 0 0 14px 0;
  max-width: 40rem;
`;

const LeadTightBottom = styled(Lead)`
  margin-bottom: 18px;
`;

/** Hero emphasis: bigger, caps, subtle archive vs. energized “utilized” look (html2pdf-friendly solids) */
const CoverEmphWord = styled.span<{ $variant: 'archive' | 'utilize' }>`
  display: inline-block;
  vertical-align: baseline;
  font-size: clamp(18px, 2.8vw, 24px);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.11em;
  line-height: 1.25;
  margin: 0 0.06em;
  ${(p) =>
    p.$variant === 'archive'
      ? `
    color: #e2e8f0;
    text-shadow: 0 2px 18px rgba(148, 163, 184, 0.45);
  `
      : `
    color: #f0f9ff;
    text-shadow:
      0 0 22px rgba(56, 189, 248, 0.55),
      0 0 42px rgba(129, 140, 248, 0.35);
    padding-bottom: 1px;
    border-bottom: 2px solid rgba(125, 211, 252, 0.55);
  `}
`;

const CoverModalityList = styled.ul`
  margin: 0 0 16px 0;
  padding-left: 1.1rem;
  font-size: 14px;
  line-height: 1.55;
  color: #cbd5e1;
  max-width: 42rem;
  columns: 1;
  @media (min-width: 640px) {
    columns: 2;
    column-gap: 28px;
  }
  li {
    break-inside: avoid;
    margin-bottom: 6px;
  }
`;

const CoverCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  align-items: center;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #e2e8f0;
`;

const TrustStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px 20px;
  padding: 12px 0 22px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const TrustItem = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  line-height: 1.45;
  text-transform: none;
  letter-spacing: 0;
  padding-left: 1.1rem;
  position: relative;
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #059669;
    font-weight: 800;
  }
`;

const Section = styled.section`
  margin-bottom: 36px;
`;

/** Groups kicker + H2 to reduce awkward splits in print */
const SectionHeadingBlock = styled.div``;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #64748b;
  margin-bottom: 8px;
  &.marketing-section-kicker {
    text-transform: none;
    letter-spacing: 0.06em;
    font-size: 12px;
  }
  /* Main brochure sections: high-attention runway labels */
  &.marketing-section-kicker.marketing-section-kicker-major {
    font-size: clamp(22px, 4vw, 34px);
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: #0f172a;
    margin-bottom: 14px;
    padding-left: 14px;
    border-left: 5px solid #2563eb;
    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.12);
    border-radius: 0 8px 8px 0;
    padding-top: 4px;
    padding-bottom: 4px;
    background: linear-gradient(90deg, rgba(37, 99, 235, 0.06) 0%, transparent 55%);
  }
`;

const H2 = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 14px 0;
  letter-spacing: -0.02em;
`;

const Body = styled.p`
  margin: 0 0 14px 0;
  font-size: 15px;
  color: #475569;
  line-height: 1.65;
`;

const Ul = styled.ul`
  margin: 0;
  padding-left: 1.15rem;
  color: #334155;
  font-size: 15px;
  line-height: 1.65;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const Card = styled.div`
  padding: 18px 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0ea5e9;
  margin-bottom: 6px;
`;

const CardText = styled.div`
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
`;

const Figure = styled.figure`
  margin: 12px 0;
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
`;

const FigHead = styled.div`
  padding: 10px 14px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
`;

const FigTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const FigSub = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const FigImgWrap = styled.div`
  padding: 8px 12px 12px;
  background: #fafafa;
`;

const FigImg = styled.img`
  display: block;
  width: auto;
  max-width: min(100%, 93%);
  max-height: 280px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
  border-radius: 6px;
`;

const FigCaption = styled.figcaption`
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
  padding: 0 14px 12px;
`;

const Placeholder = styled.div`
  min-height: 240px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
  padding: 16px;
`;

const ErrBox = styled.div`
  min-height: 192px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b91c1c;
  font-size: 13px;
  text-align: center;
  padding: 16px;
`;

const CtaBlock = styled.div`
  margin-top: 8px;
  padding: 28px 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #eef2ff 100%);
  border: 1px solid #bae6fd;
  text-align: center;
`;

const CtaTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 8px;
`;

const CtaLead = styled.p`
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #475569;
  line-height: 1.55;
`;

const CtaServices = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  text-align: left;
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
`;

const Disclaimer = styled.p`
  margin: 32px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;
  strong {
    color: #475569;
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
  }
`;

/** Sits outside PdfBody so html2pdf never captures the control (incl. “Preparing PDF…”). */
const PostBrochureExport = styled.div`
  max-width: 920px;
  margin: 20px auto 0;
  padding: 0 24px 32px;
  display: flex;
  justify-content: center;
`;

/** Clinical wording per docs/scientific_plotting/SCIENTIFIC_PLOTTING_STYLE.md, no vendor/device branding */
const FIGURE_COPY: Record<
  'neckcare-chyps' | 'chyps-marcons' | 'vng-latency-butterfly',
  { title: string; subtitle: string; caption: string }
> = {
  'neckcare-chyps': {
    title: 'Cervical proprioception control versus visual motion sensitivity',
    subtitle: 'Cross-modality correlation (scatter)',
    caption:
      'Relates cervical proprioception control to visual motion sensitivity, useful for discussing cervical, visual, and vestibular integration, not for diagnosis in isolation.',
  },
  'chyps-marcons': {
    title: 'Visual motion sensitivity versus MARCoNS result',
    subtitle: 'Sensory measure and nasal culture outcome',
    caption:
      'Exploratory view linking visual motion sensitivity to MARCoNS (nasal culture) positivity, hypothesis-generating across sensory and microbiology data.',
  },
  'vng-latency-butterfly': {
    title: 'VNG latency versus cervical proprioception control',
    subtitle: 'Dual-axis butterfly plot (latency and proprioception trajectories)',
    caption:
      'Plots vestibular/oculomotor latency with cervical proprioception control to visualize parallel trends, interpret alongside full clinical context.',
  },
};

const ClinicalMarketingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: MarketingTabId = searchParams.get('tab') === 'research' ? 'research' : 'clinical';
  const selectTab = useCallback(
    (t: MarketingTabId) => {
      if (t === 'clinical') setSearchParams({});
      else setSearchParams({ tab: 'research' });
    },
    [setSearchParams]
  );

  const [neckUrl, setNeckUrl] = useState<string | null>(null);
  const [marconsUrl, setMarconsUrl] = useState<string | null>(null);
  const [vngUrl, setVngUrl] = useState<string | null>(null);
  const [neckErr, setNeckErr] = useState<string | null>(null);
  const [marconsErr, setMarconsErr] = useState<string | null>(null);
  const [vngErr, setVngErr] = useState<string | null>(null);
  const [neckLoading, setNeckLoading] = useState(true);
  const [marconsLoading, setMarconsLoading] = useState(true);
  const [vngLoading, setVngLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let u1: string | null = null;
    let u2: string | null = null;
    let u3: string | null = null;

    setNeckLoading(true);
    setMarconsLoading(true);
    setVngLoading(true);
    setNeckErr(null);
    setMarconsErr(null);
    setVngErr(null);

    api
      .getDashboardCorrelationNeckcareChypsBlob()
      .then((blob) => {
        if (cancelled) return;
        u1 = URL.createObjectURL(blob);
        setNeckUrl(u1);
      })
      .catch((e: Error) => {
        if (!cancelled) setNeckErr(e.message || 'Failed to load plot');
      })
      .finally(() => {
        if (!cancelled) setNeckLoading(false);
      });

    api
      .getDashboardCorrelationChypsMarconsBlob()
      .then((blob) => {
        if (cancelled) return;
        u2 = URL.createObjectURL(blob);
        setMarconsUrl(u2);
      })
      .catch((e: Error) => {
        if (!cancelled) setMarconsErr(e.message || 'Failed to load plot');
      })
      .finally(() => {
        if (!cancelled) setMarconsLoading(false);
      });

    api
      .getDashboardCorrelationVngLatencyButterflyBlob()
      .then((blob) => {
        if (cancelled) return;
        u3 = URL.createObjectURL(blob);
        setVngUrl(u3);
      })
      .catch((e: Error) => {
        if (!cancelled) setVngErr(e.message || 'Failed to load plot');
      })
      .finally(() => {
        if (!cancelled) setVngLoading(false);
      });

    return () => {
      cancelled = true;
      if (u1) URL.revokeObjectURL(u1);
      if (u2) URL.revokeObjectURL(u2);
      if (u3) URL.revokeObjectURL(u3);
    };
  }, []);

  /** Download PDF (html2pdf): margins only; no browser URL/date. Page breaks below apply to this export. */
  const handleDownloadPdf = useCallback(async () => {
    const el = pdfBodyRef.current;
    if (!el) return;
    setPdfBusy(true);
    const pdfName =
      activeTab === 'research' ? 'research-partner-brochure.pdf' : 'clinician-brochure.pdf';
    try {
      await html2pdf()
        .set({
          margin: [12, 12, 16, 12],
          filename: pdfName,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true,
            ignoreElements: (node: Element) => (node as HTMLElement).classList?.contains('marketing-no-pdf') ?? false,
          },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: {
            mode: ['css', 'legacy'],
            /* Explicit breaks before major sections; .html2pdf__page-break handles Outcome after the tall Proof block */
            before: '.marketing-print-new-page',
            avoid: ['.marketing-figure', '.marketing-problem-solution-sheet'],
          },
        })
        .from(el)
        .save();
    } catch (err) {
      console.error(err);
      window.alert('PDF export failed. Try the Download PDF button again.');
    } finally {
      setPdfBusy(false);
    }
  }, [activeTab]);

  /** Browser Print → PDF injects date + URL in margins; on this page ⌘P / Ctrl+P runs our PDF instead. */
  const handleDownloadPdfRef = useRef(handleDownloadPdf);
  handleDownloadPdfRef.current = handleDownloadPdf;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 'p') return;
      e.preventDefault();
      e.stopPropagation();
      void handleDownloadPdfRef.current();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const renderPlot = (
    key: 'neckcare-chyps' | 'chyps-marcons' | 'vng-latency-butterfly',
    url: string | null,
    loading: boolean,
    err: string | null
  ) => {
    const meta = FIGURE_COPY[key];
    return (
      <Figure className="marketing-figure" key={key}>
        <FigHead className="marketing-fig-head">
          <FigTitle className="marketing-fig-title">{meta.title}</FigTitle>
          <FigSub className="marketing-fig-sub">{meta.subtitle}</FigSub>
        </FigHead>
        <FigImgWrap className="marketing-fig-img-wrap">
          {loading && <Placeholder>Loading live dashboard figure…</Placeholder>}
          {!loading && err && <ErrBox>{err}</ErrBox>}
          {!loading && !err && url && (
            <FigImg className="marketing-fig-img" src={url} alt={meta.title} />
          )}
        </FigImgWrap>
        <FigCaption className="marketing-fig-caption">{meta.caption}</FigCaption>
      </Figure>
    );
  };

  return (
    <>
      <PrintStyles />
      <Root className="marketing-print-root">
        <Toolbar className="marketing-no-print">
          <ToolbarHint>
            <strong>Download PDF</strong> or <strong>⌘P</strong> / <strong>Ctrl+P</strong> (on this page only) saves a
            clean PDF, <strong>no</strong> date, URL, or footer text. Using the browser&apos;s Print dialog → Save as PDF{' '}
            <em>always</em> adds those. For paper, use <strong>File → Print</strong> from the browser menu (shortcut is
            overridden here for PDF export).
          </ToolbarHint>
          <ToolbarActions>
            <ExportBtn type="button" onClick={handleDownloadPdf} disabled={pdfBusy}>
              {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
            </ExportBtn>
          </ToolbarActions>
        </Toolbar>

        <PortalHeader className="marketing-no-print">
          <PortalTitle>Marketing Portal</PortalTitle>
          <PortalSubtext>
            {activeTab === 'clinical'
              ? 'Clinical marketing, for hospitals, clinicians, and care teams evaluating connected analytics and operational insight.'
              : 'Research partner marketing, for med tech, device, therapeutic, and startup teams pursuing collaboration, mechanism science, and evidence generation.'}
          </PortalSubtext>
          <TabList role="tablist" aria-label="Marketing brochure audience">
            <TabButton
              type="button"
              role="tab"
              aria-selected={activeTab === 'clinical'}
              $active={activeTab === 'clinical'}
              onClick={() => selectTab('clinical')}
            >
              <span>Clinical marketing</span>
              <TabAudienceHint>Clinicians, doctors, hospitals</TabAudienceHint>
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              aria-selected={activeTab === 'research'}
              $active={activeTab === 'research'}
              onClick={() => selectTab('research')}
            >
              <span>Research partner marketing</span>
              <TabAudienceHint>Med tech, device, therapeutics & startups</TabAudienceHint>
            </TabButton>
          </TabList>
        </PortalHeader>

        <Banner className="marketing-no-print">
          <strong>Super admin preview.</strong> Figures load from the same authenticated APIs as the Dashboard. To make
          this public later, adjust routing and serve static brochure assets or a public plot endpoint.
        </Banner>

        <PdfBody ref={pdfBodyRef} className="marketing-pdf-body">
          {activeTab === 'clinical' ? (
            <>
              <div className="marketing-pdf-page-one-bundle">
                <Cover className="marketing-pdf-cover">
                  <CoverInner className="marketing-pdf-cover-inner">
                    <CoverTagline>Clinical AI and Intelligence</CoverTagline>
                    <H1>Turn clinical data into actionable insight.</H1>
                    <Lead>
                      Ingest and link modalities across your pipeline, so teams see connections, not silos. Scope includes:
                    </Lead>
                    <CoverModalityList>
                      <li>Symptom-based questionnaires and structured intake data</li>
                      <li>VNG and saccadic oculomotor measurements</li>
                      <li>Cervical proprioception assessments</li>
                      <li>Functional NIRS hemodynamics</li>
                      <li>Brain MRI and structural imaging</li>
                      <li>Gait and movement video</li>
                      <li>Laboratory panels, microbiology (e.g., nasal cultures), rehab metrics, and clinical documents</li>
                    </CoverModalityList>
                    <LeadTightBottom>
                      Modality-first, vendor-agnostic architecture. We want clinical data not only{' '}
                      <CoverEmphWord $variant="archive">Archived</CoverEmphWord>, but also{' '}
                      <CoverEmphWord $variant="utilize">Utilized</CoverEmphWord>.
                    </LeadTightBottom>
                    <CoverCtaRow>
                      <Pill>Data-driven analytics</Pill>
                      <Pill>Publication-style plots</Pill>
                      <Pill>Clinical judgment first</Pill>
                    </CoverCtaRow>
                  </CoverInner>
                </Cover>

                <TrustStrip className="marketing-pdf-trust-strip">
                  <TrustItem>
                    <strong>Data-driven workflows</strong>, dashboards and analytics grounded in your cohort, not generic
                    stock visuals.
                  </TrustItem>
                  <TrustItem>
                    <strong>Longitudinal views</strong>, including pre- and post-therapy and treatment timelines to help
                    objectify the care process.
                  </TrustItem>
                  <TrustItem>
                    <strong>Cross-sectional cohort analysis</strong>, compare brain-health populations and link modalities for
                    deeper insight.
                  </TrustItem>
                  <TrustItem>
                    <strong>Publication-ready outputs</strong>, scientific plotting, reporting, and interpretable analytics
                    for manuscripts and presentations.
                  </TrustItem>
                </TrustStrip>
              </div>

              <div className="marketing-problem-solution-sheet">
                <Section className="marketing-pdf-page-one-problem">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">The problem</SectionLabel>
                  <H2>Data everywhere, synthesis nowhere</H2>
                  <Ul>
                    <li>Modalities live in separate workflows and mindsets.</li>
                    <li>
                      Clinical data remain fragmented across scribe tools, chart systems, EHR, practice management, and other
                      point solutions, rarely connected when teams need integrated signal for day-to-day reasoning.
                    </li>
                    <li>Teams need a single operational layer, not another locked silo.</li>
                  </Ul>
                </Section>

                <Section>
                  <SectionHeadingBlock className="marketing-section-heading-block">
                    <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">The solution</SectionLabel>
                    <H2>Our&nbsp;platform: one pane for connected insight</H2>
                  </SectionHeadingBlock>
                  <Body>
                    Our platform integrates with your practice management system, EHR, and EMR to pull disparate sources into
                    one layer, correlations, modality summaries, and AI-assisted context, so rounds and follow-ups start from a
                    shared, data-informed picture.
                  </Body>
                  <Grid2>
                    <Card>
                      <CardTitle>Operational</CardTitle>
                      <CardText>
                        Dashboards and plots you can show stakeholders, grounded in your cohort, not generic stock art.
                      </CardText>
                    </Card>
                    <Card>
                      <CardTitle>Multimodal</CardTitle>
                      <CardText>Link sensory, movement, vestibular, and lab-adjacent signals in one product surface.</CardText>
                    </Card>
                    <Card>
                      <CardTitle>Trust-first</CardTitle>
                      <CardText>Supports the clinician, transparent about limits, no replacement for standard of care.</CardText>
                    </Card>
                  </Grid2>
                </Section>
              </div>

              <Section className="marketing-print-new-page">
                <SectionHeadingBlock className="marketing-section-heading-block">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">
                    Proof on the glass
                  </SectionLabel>
                  <H2>Live dashboard figures</H2>
                </SectionHeadingBlock>
                <Body>
                  The same correlation PNGs as your main Dashboard, ideal for decks, diligence, and clinical leadership
                  reviews.
                </Body>
                {renderPlot('neckcare-chyps', neckUrl, neckLoading, neckErr)}
                {renderPlot('chyps-marcons', marconsUrl, marconsLoading, marconsErr)}
                {renderPlot('vng-latency-butterfly', vngUrl, vngLoading, vngErr)}
              </Section>

              <div className="html2pdf__page-break marketing-pdf-page-break-sentinel" aria-hidden="true" />
              <Section>
                <SectionHeadingBlock className="marketing-section-heading-block">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">
                    Outcome driven care
                  </SectionLabel>
                  <H2>Objectify care, unlock the data you already collect</H2>
                </SectionHeadingBlock>
                <Grid2>
                  <Card>
                    <CardTitle>Data-driven insight for the whole treatment arc</CardTitle>
                    <CardText>
                      We focus on insight that helps clinicians objectify therapy and treatment, pre- and post-change, and at
                      points along the course of care. When patients see progress and stay engaged, teams can deliver more
                      consistent, better informed care.
                    </CardText>
                  </Card>
                  <Card>
                    <CardTitle>Turn EHR and clinic-system data into scientific leverage</CardTitle>
                    <CardText>
                      Clinical data should not sit idle in an EHR, EMR, or clinic management layer. We help you work that gold
                      mine with AI, models, and transparent analytics so results can support publication, quality initiatives,
                      and broader clinical and scientific impact.
                    </CardText>
                  </Card>
                </Grid2>
              </Section>

              <CtaBlock className="marketing-figure">
                <CtaTitle>See your data connected</CtaTitle>
                <CtaLead>
                  Schedule a walkthrough or pilot discussion. We&apos;ll map your modalities to operational views on the
                  platform.
                </CtaLead>
                <CtaServices>
                  Our professional data science team can also support{' '}
                  <strong>IRB exemption or expedited review pathways</strong> for observational and retrospective analyses,
                  plus <strong>publication-ready scientific plotting</strong>, structured <strong>data reporting</strong>, and
                  analytics packaging for manuscripts and regulatory conversations, scoped to your institution&apos;s policies
                  and legal counsel.
                </CtaServices>
              </CtaBlock>

              <Disclaimer>
                <strong>Privacy and compliance for clinical partners</strong>
                Data you integrate into our platform is processed through a de-identification engine intended to remove direct
                identifiers and personal identifiers, so clinical insight, analytics, and study-style workflows can draw on
                information that, when de-identified in line with HIPAA and applicable standards, is not treated as PHI. We
                apply administrative, technical, and operational safeguards consistent with HIPAA-oriented requirements for
                data we handle.
              </Disclaimer>
            </>
          ) : (
            <>
              <div className="marketing-pdf-page-one-bundle">
                <Cover className="marketing-pdf-cover">
                  <CoverInner className="marketing-pdf-cover-inner">
                    <CoverTagline>Research partnership · Mechanism &amp; evidence</CoverTagline>
                    <H1>Clinical validation and data-driven proof for your technology.</H1>
                    <Lead>
                      Partner with us on feasibility, pilot, and publication-ready studies. Our strength is the pairing of{' '}
                      <strong>deep clinical operations</strong> and <strong>professional data science</strong>: well-phenotyped
                      cohorts, structured workflows, and objective biomarkers in real clinic practice, together with
                      statistical modeling, reproducible analytics, and publication-quality reporting, so you can validate
                      your science with analyzable cohort data grounded in clinical workflow.
                    </Lead>
                    <CoverModalityList>
                      <li>Structured clinical outcomes and intervention tracking</li>
                      <li>Oculomotor and brainstem-relevant eye-tracking signals</li>
                      <li>fNIRS hemodynamics and cortical response</li>
                      <li>VNG, cervical proprioception, questionnaires, labs, imaging, gait video</li>
                      <li>Correlation analytics, modeling, and publication-style figures on live cohort data</li>
                    </CoverModalityList>
                    <LeadTightBottom>
                      <CoverEmphWord $variant="archive">Clinical</CoverEmphWord> access and protocol execution, plus{' '}
                      <CoverEmphWord $variant="utilize">data science</CoverEmphWord> rigor from dataset to manuscript and
                      regulatory-ready summaries, aligned to your indication.
                    </LeadTightBottom>
                    <CoverCtaRow>
                      <Pill>Patient access</Pill>
                      <Pill>Clinic-scale execution</Pill>
                      <Pill>Mechanism science</Pill>
                    </CoverCtaRow>
                  </CoverInner>
                </Cover>

                <TrustStrip className="marketing-pdf-trust-strip">
                  <TrustItem>
                    <strong>Migraine and brain health cohort depth</strong> through FNC and NWHSU-aligned access pathways.
                  </TrustItem>
                  <TrustItem>
                    <strong>Operational rigor</strong>, protocol-driven capture embedded in clinic workflow, not a one-off
                    academic extract.
                  </TrustItem>
                  <TrustItem>
                    <strong>Data science and evidence path</strong>, statistics, reproducible pipelines, and publication-ready
                    outputs from feasibility and pilot work through packages suited to publication and regulatory
                    conversations, alongside clinical operations.
                  </TrustItem>
                  <TrustItem>
                    <strong>Objective physiology</strong>, oculomotor / eye-tracking and fNIRS on top of structured clinical phenotypes.
                  </TrustItem>
                </TrustStrip>
              </div>

              <div className="marketing-problem-solution-sheet">
                <Section className="marketing-pdf-page-one-problem">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">Patient access</SectionLabel>
                  <H2>Reach the patients your science needs</H2>
                  <Ul>
                    <li>Migraine and brain health cohorts via FNC + NWHSU.</li>
                    <li>Well-phenotyped patients (chronic, post-concussive, vestibular).</li>
                    <li>Potential for longitudinal follow-up.</li>
                  </Ul>
                </Section>

                <Section>
                  <SectionHeadingBlock className="marketing-section-heading-block">
                    <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">Execution</SectionLabel>
                    <H2>From protocol to dataset, without fighting the clinic</H2>
                  </SectionHeadingBlock>
                  <Ul>
                    <li>Protocol-driven data collection embedded in clinic workflow.</li>
                    <li>Scalable support from trained students.</li>
                    <li>Standardized clinical outcomes + intervention tracking.</li>
                    <li>Path from feasibility → pilot → publication.</li>
                  </Ul>
                </Section>

                <Section>
                  <SectionHeadingBlock className="marketing-section-heading-block">
                    <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">Mechanism layer</SectionLabel>
                    <H2>Link stimulation, physiology, and outcomes</H2>
                  </SectionHeadingBlock>
                  <Ul>
                    <li>Add objective biomarkers on top of clinical data.</li>
                    <li>Oculomotor / eye-tracking → eye drift, oculomotor / brainstem signals.</li>
                    <li>fNIRS → hemodynamic / cortical response.</li>
                    <li>Link: stimulation → physiology → outcomes.</li>
                  </Ul>
                </Section>
              </div>

              <Section className="marketing-print-new-page">
                <SectionHeadingBlock className="marketing-section-heading-block">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">
                    Objectify the indication
                  </SectionLabel>
                  <H2>FDA-facing and investor-ready evidence, grounded in real patients</H2>
                </SectionHeadingBlock>
                <Body>
                  We help you articulate what your device, diagnostic, therapeutic, or intervention actually changes in
                  clinical practice, across diagnosis, treatment, therapy, and longitudinal care. Then we measure it with
                  the same discipline you need for regulators and capital markets.
                </Body>
                <Grid2>
                  <Card>
                    <CardTitle>Indication &amp; endpoints</CardTitle>
                    <CardText>
                      Sharpen whether you are targeting diagnosis, treatment, therapy, or a specific intervention window,
                      and map endpoints to phenotypes and biomarkers we can operationalize in clinic.
                    </CardText>
                  </Card>
                  <Card>
                    <CardTitle>Value prop &amp; clinical impact</CardTitle>
                    <CardText>
                      Translate mechanism hypotheses into structured outcomes and objective physiology, so clinical impact and
                      efficacy read through in analyzable data as well as in the narratives you share with stakeholders.
                    </CardText>
                  </Card>
                  <Card>
                    <CardTitle>Partnership to FDA &amp; investors</CardTitle>
                    <CardText>
                      Use our services, or a deeper research partnership, to build the evidence trail from feasibility through
                      pilot that supports FDA conversations, due diligence, and investor storytelling with defensible clinical
                      evidence.
                    </CardText>
                  </Card>
                </Grid2>
              </Section>

              <Section className="marketing-print-new-page">
                <SectionHeadingBlock className="marketing-section-heading-block">
                  <SectionLabel className="marketing-section-kicker marketing-section-kicker-major">
                    Proof on the glass
                  </SectionLabel>
                  <H2>Live dashboard figures</H2>
                </SectionHeadingBlock>
                <Body>
                  The same correlation PNGs as your main Dashboard, representative of the multimodal signal we can align to
                  your protocol and endpoints.
                </Body>
                {renderPlot('neckcare-chyps', neckUrl, neckLoading, neckErr)}
                {renderPlot('chyps-marcons', marconsUrl, marconsLoading, marconsErr)}
                {renderPlot('vng-latency-butterfly', vngUrl, vngLoading, vngErr)}
              </Section>

              <div className="html2pdf__page-break marketing-pdf-page-break-sentinel" aria-hidden="true" />
              <CtaBlock className="marketing-figure">
                <CtaTitle>Explore a research partnership</CtaTitle>
                <CtaLead>
                  Tell us your mechanism question, device class, and timeline. We&apos;ll map cohort fit, capture burden,
                  biomarker stack, and a staged plan from feasibility to publication-quality outputs.
                </CtaLead>
                <CtaServices>
                  Our team supports <strong>IRB-aligned study design</strong>,{' '}
                  <strong>publication-ready scientific plotting</strong>, structured <strong>data reporting</strong>, and
                  analytics packaging suited to regulatory and investor diligence. Results and statistical summaries can be
                  framed as <strong>FDA-ready</strong> deliverables to support sponsor workflows:{' '}
                  <strong>Pre-Submission (Pre-Sub)</strong> meetings, <strong>Q-Submission (Q-Sub)</strong> materials, and
                  FDA-requested documentation, tables, figures, and written <strong>justifications</strong> that underpin
                  evidence of <strong>substantial equivalence</strong> for a <strong>510(k)</strong>, or narrative and
                  performance evidence aligned with <strong>De Novo</strong> and <strong>Breakthrough Device designation</strong>{' '}
                  strategies where applicable, all scoped to your counsel, QA/RA, and institutional requirements.
                </CtaServices>
              </CtaBlock>
            </>
          )}
        </PdfBody>

        <PostBrochureExport className="marketing-no-print marketing-no-pdf">
          <ExportBtn type="button" onClick={handleDownloadPdf} disabled={pdfBusy} className="marketing-no-pdf">
            {pdfBusy ? 'Preparing PDF…' : 'Download this brochure (PDF)'}
          </ExportBtn>
        </PostBrochureExport>
      </Root>
    </>
  );
};

export default ClinicalMarketingPage;
